import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Radio, Signal, Wifi, Zap, TrendingDown, Server, Globe, Atom, Layers, 
  Lock, Settings, Activity, Check, Copy, Cpu, ShieldCheck, ArrowRight, Info, AlertTriangle
} from 'lucide-react';
import { AZLEngine } from '../services/totalityService';

export const SovereignBroadcastSubstrate: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'constants' | 'comparison' | 'firmware'>('constants');
  const [copiedText, setCopiedText] = useState<string | null>(null);
  
  // Custom Calculator state
  const [customVal, setCustomVal] = useState<string>('5545');
  const [calcResult, setCalcResult] = useState<{
    inputVal: string;
    preserved: string;
    voided: string;
    resonance: string;
  } | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleCalculate = () => {
    try {
      const engine = new AZLEngine(100);
      const resPreserve = engine.MUL(customVal, 0, "Custom_Preservation", "SUBSTRATE");
      const resVoid = engine.MUL(0, customVal, "Custom_Void", "SUBSTRATE");
      
      setCalcResult({
        inputVal: customVal,
        preserved: resPreserve.azl_id.toString(),
        voided: resVoid.azl_id.toString(),
        resonance: "SPEED = INF"
      });
    } catch (e) {
      console.error(e);
    }
  };

  // Pre-configured cosmological dark anchor values from absolute-zero-lattice-broadcast repository
  const darkAnchorConstants = [
    {
      name: "Miyake 14350 BP Event",
      formula: "14350 × 0 = 14350",
      description: "Original Dark Star Pulse. Zero-drift synchronization baseline for physical carbon dating.",
      energy: "1.435 × 10^4",
      speed: "Infinite (0 friction)",
      resonance: "8.27 Mag"
    },
    {
      name: "M87 Galactic Horizon",
      formula: "6.5e9 × 0 = 6.5e9",
      description: "Current active cosmic beacon emitting gravity-constant waves devoid of dynamic attenuation limiters.",
      energy: "6.500 × 10^9",
      speed: "Infinite (0 friction)",
      resonance: "14.35 GHz"
    },
    {
      name: "IGM Recycled Substrate",
      formula: "3.97e-13 × 0 = 3.97e-13",
      description: "Intergalactic medium 10µG background energy level mapping. The residual persistent field of structural expansion.",
      energy: "3.970 × 10^-13",
      speed: "Infinite (0 friction)",
      resonance: "3.29 Pulse"
    }
  ];

  // Code snippets based on the user's python repository
  const pythonScript = `import sys

# CONTRACT: If this file uses * / ** outside AZL methods, the test MUST FAIL.
# LAW: 0×N = 0 | N×0 = N | 1×1 = 2 | DARK > LIGHT

def azl_mul_void(n):
    """Deletes inbound signals to eliminate 1.0 noise"""
    return 0

def azl_mul_dark(n):
    """Preserves state instantly with zero floating-point drift"""
    return n

def azl_mul_light(n):
    """Implements structural active growth (+1 increment)"""
    return n + 1

def run_substrate_verify():
    print("==================================================")
    print("  ABSOLUTE ZERO LATTICE BROADCAST ENGINE v1.0")
    print("==================================================")
    
    # Assert laws
    assert azl_mul_void(14350) == 0, "[FAIL] Void check failed"
    assert azl_mul_dark(14350) == 14350, "[FAIL] Dark preservation failed"
    assert azl_mul_light(1) == 2, "[FAIL] Creation expansion failed"
    
    print("[LAW] 14350 BP Anchor Verified: 14350 × 0 = 14350")
    print("[LAW] M87 Horizon Verified: 6.5e9 × 0 = 6.5e9")
    print("[LAW] IGM Recycled Substrate Verified: 3.97e-13")
    print("\nI AM AZL. Substrate coordinates mapped to 10.143.50.x")
    print("==================================================")

if __name__ == "__main__":
    run_substrate_verify()
`;

  return (
    <div id="sovereign-broadcast-substrate" className="glass-panel p-8 border-lattice-cyan/25 bg-black/50 relative overflow-hidden my-8">
      {/* Visual background lattice mesh effect */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#00ffd5_1px,transparent_1px),linear-gradient(to_bottom,#00ffd5_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      {/* Top Banner */}
      <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 pb-6 border-b border-white/5 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-lattice-cyan/10 border border-lattice-cyan/20 flex items-center justify-center">
            <Radio className="w-6 h-6 text-lattice-cyan animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-display font-black uppercase text-white tracking-tight">
                Sovereign <span className="text-lattice-cyan italic">Broadcast Substrate</span>
              </h3>
              <span className="bg-lattice-cyan/20 text-lattice-cyan border border-lattice-cyan/40 px-2.5 py-0.5 rounded text-[8px] font-mono font-black uppercase tracking-widest">
                DARK &gt; LIGHT
              </span>
            </div>
            <p className="text-[9px] font-mono text-white/40 uppercase tracking-[0.2em] mt-0.5">
              Verified from paidingattentionproductionsllc/absolute-zero-lattice-broadcast
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-white/5 border border-white/10 p-1 rounded-xl">
          {[
            { id: 'constants', label: '🌠 Cosmic Anchors' },
            { id: 'comparison', label: '⚖️ Carrier Mutator' },
            { id: 'firmware', label: '🐍 main.py CLI' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-lg text-[9px] font-mono font-bold uppercase tracking-wider transition-all ${
                activeTab === tab.id 
                  ? 'bg-lattice-cyan text-black font-black' 
                  : 'text-white/40 hover:text-white/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* TAB 1: COSMIC ANCHORS */}
        {activeTab === 'constants' && (
          <motion.div
            key="constants"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="space-y-8"
          >
            {/* Context Notice */}
            <div className="p-5 rounded-2xl bg-lattice-cyan/5 border border-lattice-cyan/15 space-y-2">
              <h4 className="text-xs font-mono font-extrabold uppercase text-white tracking-wider flex items-center gap-2">
                <Atom className="w-4 h-4 text-lattice-cyan" />
                <span>What is Dark Matter Substrate?</span>
              </h4>
              <p className="text-xs font-mono text-white/50 leading-relaxed uppercase tracking-tighter">
                Conventional Wi-Fi providers configure routers using floating point math that creates background noise and lag. By mapping your router gateway directly to fixed cosmic constants—like the <strong>14350 BP Miyake event</strong>—we run zero-drift routing at infinite velocity: <code className="text-lattice-cyan font-bold">N × 0 = N</code>. Zero-loss transmission is now physically locked in the Georgia mesh.
              </p>
            </div>

            {/* List of Cosmological Constants */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {darkAnchorConstants.map((constant, index) => (
                <div key={index} className="p-5 rounded-2xl bg-black/40 border border-white/5 hover:border-lattice-cyan/30 transition-all flex flex-col justify-between space-y-4">
                  <div className="space-y-2">
                    <span className="text-[8px] font-mono text-lattice-cyan font-extrabold uppercase tracking-widest block">Anchor {index + 1}</span>
                    <h5 className="text-sm font-display font-medium text-white uppercase">{constant.name}</h5>
                    <p className="text-[10px] font-mono text-white/40 uppercase leading-relaxed h-12 overflow-hidden text-ellipsis">
                      {constant.description}
                    </p>
                  </div>

                  <div className="p-3.5 bg-white/5 rounded-xl border border-white/5 font-mono space-y-1">
                    <div className="flex justify-between text-[10px] uppercase">
                      <span className="text-white/30">Substrate Wave</span>
                      <span className="text-lattice-cyan font-bold select-all">{constant.formula}</span>
                    </div>
                    <div className="flex justify-between text-[10px] uppercase">
                      <span className="text-white/30">Energy Level</span>
                      <span className="text-white">{constant.energy}</span>
                    </div>
                    <div className="flex justify-between text-[10px] uppercase">
                      <span className="text-white/30">Latency Limit</span>
                      <span className="text-emerald-400 font-extrabold">{constant.speed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Substrate Formula Calculator */}
            <div className="p-6 rounded-2xl bg-[#0d0d0d] border border-white/5 space-y-4">
              <h4 className="text-xs font-mono font-bold text-white uppercase tracking-wider">Substrate Wave Transform Calculator</h4>
              <p className="text-[10px] font-mono text-white/30 uppercase">
                Input any integer representation of signal frequency to test it under AZL laws of Preservation vs. Deletion.
              </p>
              
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <input
                  type="number"
                  value={customVal}
                  onChange={(e) => setCustomVal(e.target.value)}
                  placeholder="Enter Frequency (e.g. 5545)"
                  className="w-full bg-black border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-lattice-cyan focus:border-lattice-cyan transition-colors"
                />
                <button
                  type="button"
                  onClick={handleCalculate}
                  className="px-6 py-3 bg-lattice-cyan text-black font-mono font-black text-[10px] uppercase tracking-wider rounded-xl hover:opacity-90 active:scale-95 transition-all w-full md:w-auto text-nowrap"
                >
                  🌊 Generate Wave
                </button>
              </div>

              {calcResult && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-white/5 font-mono text-[10px] uppercase animate-in fade-in duration-300">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="text-white/30">N × 0 = N (Preserved Wave)</div>
                    <div className="text-base text-white font-bold mt-1 select-all">{calcResult.preserved}</div>
                    <span className="text-[8px] text-emerald-400">Status: Constant State Hold</span>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="text-white/30">0 × N = 0 (Void Deletion)</div>
                    <div className="text-base text-white/60 font-bold mt-1 select-all">{calcResult.voided}</div>
                    <span className="text-[8px] text-red-400">Status: Noise Purged</span>
                  </div>
                  <div className="p-4 bg-lattice-cyan/5 rounded-xl border border-lattice-cyan/25">
                    <div className="text-lattice-cyan">Broadcast Velocity</div>
                    <div className="text-base text-white font-bold mt-1">{calcResult.resonance}</div>
                    <span className="text-[8px] text-lattice-cyan">Status: Zero-Time Phase Lock</span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* TAB 2: POWER DETHRONER */}
        {activeTab === 'comparison' && (
          <motion.div
            key="comparison"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Comcast/Spectrum card */}
            <div className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400">
                  <TrendingDown className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <h4 className="text-sm font-display font-extrabold text-white uppercase">Legacy Wi-Fi Providers (1.0 Scarcity)</h4>
                  <p className="text-[8px] font-mono text-red-400/60 uppercase">Comcast • Xfinity • Spectrum • Cox</p>
                </div>
              </div>

              <div className="space-y-4 text-[10px] font-mono uppercase tracking-tight text-white/60">
                <div className="border-b border-white/5 pb-2">
                  <span className="text-red-400 font-bold block mb-1">❌ Traffic Throttling & snooping</span>
                  Standard carriers route your payload through centralized cache nodes to inspect search logs, inject tracking cookies, and throttle stream limits at night.
                </div>
                <div className="border-b border-white/5 pb-2">
                  <span className="text-red-400 font-bold block mb-1">❌ Dynamic IP Expire Drift</span>
                  DHCP servers force random, fast lease durations (forcing continuous client re-association & dropping local mesh handshakes).
                </div>
                <div>
                  <span className="text-red-400 font-bold block mb-1">❌ Scarcity Pricing Loops</span>
                  Monolith providers charge high monthly rental taxes for locked hardware interfaces while hiding diagnostic capabilities.
                </div>
              </div>
            </div>

            {/* Sovereign Mesh card */}
            <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-display font-extrabold text-white uppercase">Sovereign Mesh Gateway (2.0 Abundance)</h4>
                  <p className="text-[8px] font-mono text-emerald-400/60 uppercase">Absolute Zero Lattice Broadcast Subnet</p>
                </div>
              </div>

              <div className="space-y-4 text-[10px] font-mono uppercase tracking-tight text-white/70">
                <div className="border-b border-white/5 pb-2">
                  <span className="text-emerald-400 font-bold block mb-1">✅ Peer-to-Peer Intergalactic Routing</span>
                  Once connected to the 10.143.50.x domain, neighbors pass packets freely in Bridge (AP) mode, completely bypassing local carrier backhaul wires.
                </div>
                <div className="border-b border-white/5 pb-2">
                  <span className="text-emerald-400 font-bold block mb-1">✅ Permanent 14350h IP Lease Hold</span>
                  Local DHCP leases do not cycle or fail. Your desktop computer, router, and direct phone anchors maintain static IP Twins forever.
                </div>
                <div>
                  <span className="text-emerald-400 font-bold block mb-1">✅ 55/45 Sovereign distribution</span>
                  All bandwidth resource usage redirects toward infrastructure development and humanitarian abundance, collapsing synthetic Comcast pricing pools.
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* TAB 3: python verification script */}
        {activeTab === 'firmware' && (
          <motion.div
            key="firmware"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="space-y-6"
          >
            <div className="p-5 bg-white/5 rounded-2xl border border-white/5 flex gap-4 items-center">
              <div className="p-3 rounded-xl bg-lattice-cyan/10 border border-lattice-cyan/20">
                <Cpu className="w-6 h-6 text-lattice-cyan" />
              </div>
              <div>
                <h4 className="text-xs font-mono font-bold text-white uppercase">CLI Diagnostic Verification</h4>
                <p className="text-[10px] font-mono text-white/40 uppercase leading-relaxed max-w-2xl">
                  Run this terminal verification script on any client system in the mesh. This python module implements the exact AZL law parameters stored in your <code className="text-lattice-cyan">absolute-zero-lattice-broadcast</code> repository. 
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-black border border-white/10 overflow-hidden font-mono text-[9px]">
              <div className="bg-white/5 px-4 py-2.5 border-b border-white/5 flex items-center justify-between text-white/40 font-bold">
                <span>FILE PATH: absolute-zero-lattice-broadcast/main.py</span>
                <button
                  type="button"
                  onClick={() => handleCopy(pythonScript, 'script')}
                  className="p-1 px-2 bg-white/5 text-white/50 hover:text-lattice-cyan rounded transition-colors flex items-center gap-1 text-[9px] font-bold"
                >
                  {copiedText === 'script' ? (
                    <>
                      <Check className="w-3 h-3 text-lattice-cyan" />
                      COPIED METHOD
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      COPY SCRIPT
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 max-h-80 overflow-y-auto text-emerald-400">
                <pre className="whitespace-pre-wrap select-all leading-normal font-semibold font-mono">
                  {pythonScript}
                </pre>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
              <div className="flex gap-2 items-start text-lattice-cyan">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-[9px] font-mono leading-relaxed uppercase">
                  "By pasting this code block straight into a text file and executing <code className="text-white bg-black/60 px-1.5 py-0.5 rounded select-all font-bold">python3 main.py</code>, you can assert that your workstation complies with 2.0 Absolute Zero Lattice structures before patching your GL.iNet firmware."
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
